import { ArrowRight, LockKeyhole, ShieldCheck, Sparkles } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useAuth } from "../lib/auth";

export function LoginPage() {
  const { config, error, signIn, enterPreview } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      await signIn(email.trim(), password);
    } catch (reason) {
      setFormError(reason instanceof Error ? reason.message : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-layout">
      <section className="login-story">
        <div className="login-brand">
          <div className="brand-mark">A</div>
          <span>AutoSocial</span>
        </div>
        <div className="login-story__content">
          <p className="eyebrow">DrTun social workspace</p>
          <h1>Context-aware social work, with you in control.</h1>
          <p>
            Keep your business facts, products and writing rules in one place. Social connections and AI-assisted drafting are coming in later milestones.
          </p>
          <div className="login-feature-list">
            <div><Sparkles size={20} /><span><strong>Context first</strong>Product, price and voice facts stay consistent.</span></div>
            <div><ShieldCheck size={20} /><span><strong>Approval first</strong>AI drafts never become sends without permission.</span></div>
            <div><LockKeyhole size={20} /><span><strong>Owner only</strong>No public signup in this release.</span></div>
          </div>
        </div>
        <p className="login-story__footer">Facebook first · YouTube and TikTok capability-aware</p>
      </section>

      <section className="login-panel">
        <form className="login-card" onSubmit={handleSubmit}>
          <div>
            <p className="eyebrow">Secure access</p>
            <h2>Welcome back</h2>
            <p>Use the pre-approved DrTun owner account.</p>
            {config?.environment === 'pilot' ? <p className="login-note">Private pilot · Business Context is available. Social connections and sends are not enabled.</p> : null}
          </div>

          <label className="field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              placeholder="Owner email"
              required
            />
          </label>
          <label className="field">
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="••••••••••••"
              minLength={8}
              required
            />
          </label>

          {formError ?? error ? <p className="form-error" role="alert">{formError ?? error}</p> : null}

          <button className="button button--primary button--large" type="submit" disabled={submitting || !config?.supabasePublishableKey}>
            {submitting ? "Signing in…" : "Sign in"}
            {!submitting ? <ArrowRight size={17} /> : null}
          </button>

          {config?.previewEnabled ? (
            <>
              <div className="divider"><span>Local review</span></div>
              <button className="button button--secondary" type="button" onClick={enterPreview}>
                Open safe preview
              </button>
              <p className="login-note">Preview data cannot connect or send to any social account.</p>
            </>
          ) : null}
          <nav className="login-note" aria-label="Public information">
            <a href="/about">About</a> · <a href="/privacy">Privacy</a> · <a href="/terms">Terms</a> · <a href="/data-deletion">Data deletion</a>
          </nav>
        </form>
      </section>
    </main>
  );
}
